import React, {
  useContext,
  useCallback,
  useState,
  useLayoutEffect,
  useMemo
} from "react";
import i18next from "i18next";
import cn from "clsx";
import memoize from "memoize-one";
import { validate } from "objectro";
import { NOTIFICATION_ACTION, AppConfig } from "../../types";
import {
  get,
  adjustDate,
  findByPropValueFn,
  calcTotalValidProperties,
  has
} from "../../utils/helpers";
import api from "../../utils/api";
import { loadCountries } from "../../utils/api/countries";
import { timeAgoFormatters, loadTimeAgoFormatter } from "../../utils/i18n";
import { Notification as INotification } from "../../utils/NotificationsStore";
import useT from "../../hooks/useT";
import useAsyncRetryMax from "../../hooks/useAsyncRetryMax";
import useNotifications from "../../hooks/useNotifications";
import { CustomerDataStateObject } from "../../hooks/useCustomerDataState";
import LoadingScreen from "../../components/LoadingScreen";
import Page from "../../components/Page";
import Icon from "../../components/Icon";
import Button from "../../components/Button";
import InputCheckbox from "../../components/InputCheckbox";
import Notification from "../../components/Notification";
import Modal from "../../components/Modal";
import Field from "../../components/Field";
import PaginationDropDown from "../../components/PaginationDropDown";
import AppLayoutHeaderLeftCancel from "../AppLayout/AppLayoutHeaderLeftCancel";
import AppLayout from "../../containers/AppLayout";
import { AppStateContext } from "../App";
import "./NotificationsPage.css";

interface NotificationFilters {
  [filterKey: string]: any;
  date: string;
  dateFrom: string;
  dateTo: string;
  statusType: string;
  givenName: string;
  familyName: string;
  onlyMine: boolean;
}

interface NotificationFilterQuery {
  [filterKey: string]: any;
}

interface NotificationFilterOptions {
  [optionKey: string]: any;
}

type ClickClearFieldHandlerFactory = (
  fieldKey: string
) => React.MouseEventHandler<HTMLInputElement>;

const NOTIFICATIONS_PER_PAGE = (config: AppConfig) =>
  config.notifications.showPerPage || 30;
const NOTIFICATIONS_TIME_EXPIRES_IN_DAYS = (config: AppConfig) =>
  Math.ceil(config.notifications.timeExpires / (1000 * 60 * 60 * 24));

const DEFAULT_FILTERS: NotificationFilters = {
  date: "",
  dateFrom: "",
  dateTo: "",
  statusType: "",
  givenName: "",
  familyName: "",
  onlyMine: false
};

const getNotificationActionOptions = (config: AppConfig) =>
  config.customise.searchCustomer.enable
    ? {
        customerCreated: NOTIFICATION_ACTION.CUSTOMER_DATA_CREATED,
        customerUpdated: NOTIFICATION_ACTION.CUSTOMER_DATA_UPDATED,
        allErrors: [
          NOTIFICATION_ACTION.CUSTOMER_DATA_FAILURE,
          NOTIFICATION_ACTION.CUSTOMER_DATA_API_ERROR
        ],
        errorFailure: NOTIFICATION_ACTION.CUSTOMER_DATA_FAILURE,
        errorApi: NOTIFICATION_ACTION.CUSTOMER_DATA_API_ERROR
      }
    : {
        customerCreated: NOTIFICATION_ACTION.CUSTOMER_DATA_CREATED,
        allErrors: [
          NOTIFICATION_ACTION.CUSTOMER_DATA_FAILURE,
          NOTIFICATION_ACTION.CUSTOMER_DATA_API_ERROR
        ],
        errorFailure: NOTIFICATION_ACTION.CUSTOMER_DATA_FAILURE,
        errorApi: NOTIFICATION_ACTION.CUSTOMER_DATA_API_ERROR
      };

const DATE_OPTIONS: NotificationFilterOptions = {
  today: "today",
  yesterday: "yesterday",
  daysAgo2: "daysAgo2",
  daysAgo3: "daysAgo3",
  daysAgo4: "daysAgo4",
  daysAgo5: "daysAgo5",
  daysAgo6: "daysAgo6",
  daysAgo7: "daysAgo7"
};

/** Customer Data defined in Niveau Schema, but currently unused in Customer Form */
const FILTERED_LABELS = Object.freeze([
  "alternateGivenName1",
  "alternateGivenName2",
  "alternateFamilyName1",
  "alternateFamilyName2",
  "identifier",
  "salesPerson",
  "creationStore"
]);

/** Map of ConfigurableForm field names to translation keys used in Google Translation sheets:
 * { fieldName: translationKey }*/
const REWRITE_LABELS: { [key: string]: string } = Object.freeze({
  telephone: "phone",
  mobileTelephone: "mobilePhone",
  addressCountry: "address.country",
  addressLocality: "address.city",
  addressRegion: "address.region",
  streetAddress1: "address.line1",
  streetAddress2: "address.line2",
  streetAddress3: "address.line3",
  postalCode: "address.postCode"
});

/** Customer Consent Optin field names */
const CONSENT_KEYS = Object.freeze([
  "appsConsent",
  "emailConsent",
  "phoneConsent",
  "postConsent"
]);

const generateDateOptions = memoize((lng: string, t: i18next.TFunction) =>
  Object.keys(DATE_OPTIONS).map((dateKey: string) => ({
    label: t(`notifications.filters.fields.date.options.${dateKey}.label`, {
      lng
    }),
    value: dateKey
  }))
);

const generateStatusTypeOptions = memoize(
  (lng: string, t: i18next.TFunction, config: AppConfig) =>
    Object.keys(getNotificationActionOptions(config)).map(
      (statusTypeKey: string) => ({
        label: t(
          `notifications.filters.fields.statusType.options.${statusTypeKey}.label`,
          { lng }
        ),
        value: statusTypeKey
      })
    )
);

export default function NotificationsPage() {
  // Shared context/state
  const { t, i18n } = useT();
  const [appState] = useContext(AppStateContext);
  const { checkNotifications, notificationsStore } = useNotifications();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [page, setPage] = useState(1);
  const [viewCustomerId, setViewCustomerId] = useState<string>("");
  const config = appState.config;
  const NOTIFICATION_ACTION_OPTIONS: NotificationFilterOptions = getNotificationActionOptions(
    config
  );

  // Load the timeAgoFormatter based on the current language
  const formatter = useAsyncRetryMax(
    () => loadTimeAgoFormatter(i18n.language),
    3,
    [i18n.language]
  );

  // Download the customer data only if a customer ID was set
  const customerData = useAsyncRetryMax<CustomerDataStateObject | null>(
    async () => {
      if (
        config.customise.notifications.enableDisplayCustomer &&
        viewCustomerId
      ) {
        return api.getCustomerById(viewCustomerId);
      }
      return null;
    },
    3,
    [viewCustomerId]
  );

  const countries = useAsyncRetryMax(() => loadCountries(), 3, []);

  // Memoized values
  const dateOptions = generateDateOptions(i18n.language, t);
  const statusTypeOptions = generateStatusTypeOptions(i18n.language, t, config);
  const numAppliedFilters = useMemo(() => calcTotalValidProperties(filters), [
    filters
  ]);
  const notifications = useMemo(() => {
    const _notifications = notificationsStore.store;
    const {
      dateFrom,
      dateTo,
      statusType,
      givenName,
      familyName,
      onlyMine
    } = filters;
    let filterQuery: NotificationFilterQuery = {};
    let activeFilters = 0;

    // No filters applied, show all
    if (
      !dateFrom &&
      !dateTo &&
      !statusType &&
      !givenName &&
      !familyName &&
      !onlyMine
    ) {
      return _notifications;
    }

    // Dates
    // -- Show notifications within a certain date
    // reset hour to consider time zone
    const minDate = dateFrom ? +new Date(dateFrom).setHours(0) : null;
    const maxDate = dateTo ? +new Date(dateTo).setHours(0) : null;

    if (dateFrom && dateTo) {
      activeFilters++;
      filterQuery.timestamp = {
        insideRange: {
          min: minDate,
          max: maxDate
        }
      };
    }
    // -- Show only notifications after a certain date
    else if (dateFrom && !dateTo) {
      activeFilters++;
      filterQuery.timestamp = {
        gte: minDate
      };
    }
    // -- Show only notifications before a certain date
    else if (!dateFrom && dateTo) {
      activeFilters++;
      filterQuery.timestamp = {
        lte: maxDate
      };
    }

    if (statusType && NOTIFICATION_ACTION_OPTIONS.hasOwnProperty(statusType)) {
      activeFilters++;
      filterQuery.action = {
        includesAny: NOTIFICATION_ACTION_OPTIONS[statusType]
      };
    }

    if (givenName) {
      activeFilters++;
      filterQuery["params.givenName"] = {
        startsWith: givenName
      };
    }

    if (familyName) {
      activeFilters++;
      filterQuery["params.familyName"] = {
        startsWith: familyName
      };
    }

    if (onlyMine) {
      activeFilters++;
      filterQuery.salesPersonId = {
        eqs: appState.salesPersonId
      };
    }

    if (!activeFilters) {
      return _notifications;
    }

    return _notifications.filter(n =>
      validate(n, { match: filterQuery }, { matchAll: true })
    );
  }, [checkNotifications, notificationsStore.store, filters]);

  // Slice the filtered notifications per page
  const paginatedNotifications = useMemo(() => {
    const startIndex = (page - 1) * NOTIFICATIONS_PER_PAGE(config);
    const endIndex = startIndex + NOTIFICATIONS_PER_PAGE(config);
    return notifications.slice(startIndex, endIndex);
  }, [notifications, page]);

  const maxPages = useMemo(
    () => Math.ceil(notifications.length / NOTIFICATIONS_PER_PAGE(config)),
    [notifications]
  );

  const setFilter = useCallback(
    (name: string, value: any) => {
      const updateFilters = { ...filters, [name]: value };
      setFilters(updateFilters);
    },
    [filters]
  );

  const handleChangeFieldInput = useCallback<React.ChangeEventHandler>(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(event.target.name, event.target.value);
    },
    [setFilter, filters]
  );

  const handleChangeInputSelectCustomFn = useCallback(
    (fieldKey: string) => (newValue: any) => {
      const updateFilters = {
        ...filters
      };

      // Clear filter
      if (!newValue) {
        switch (fieldKey) {
          case "date":
            updateFilters.date = "";
            updateFilters.dateFrom = "";
            updateFilters.dateTo = "";
            setFilters(updateFilters);
            break;

          default:
            setFilter(fieldKey, "");
            break;
        }
      }
      // Set filter value
      else {
        switch (fieldKey) {
          case "date":
            const today = new Date();
            updateFilters.date = String(newValue);

            switch (newValue) {
              case "today":
                updateFilters.dateFrom = adjustDate(today, 0);
                updateFilters.dateTo = adjustDate(today, 1);
                break;

              case "yesterday":
                updateFilters.dateFrom = adjustDate(today, -1);
                updateFilters.dateTo = adjustDate(today, 0);
                break;

              case "daysAgo2":
                updateFilters.dateFrom = adjustDate(today, -2);
                updateFilters.dateTo = adjustDate(today, -1);
                break;

              case "daysAgo3":
                updateFilters.dateFrom = adjustDate(today, -3);
                updateFilters.dateTo = adjustDate(today, -2);
                break;

              case "daysAgo4":
                updateFilters.dateFrom = adjustDate(today, -4);
                updateFilters.dateTo = adjustDate(today, -3);
                break;

              case "daysAgo5":
                updateFilters.dateFrom = adjustDate(today, -5);
                updateFilters.dateTo = adjustDate(today, -4);
                break;

              case "daysAgo6":
                updateFilters.dateFrom = adjustDate(today, -6);
                updateFilters.dateTo = adjustDate(today, -5);
                break;

              case "daysAgo7":
                updateFilters.dateFrom = adjustDate(today, -7);
                updateFilters.dateTo = adjustDate(today, -6);
                break;

              default:
                updateFilters.dateFrom = "";
                updateFilters.dateTo = "";
                break;
            }

            setFilters(updateFilters);
            break;

          default:
            setFilter(fieldKey, newValue);
            break;
        }
      }
    },
    [setFilter, filters]
  );

  const handleChangeFieldInputCheckbox = useCallback<React.ChangeEventHandler>(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fieldKey = event.target.name;
      const fieldValue = filters[fieldKey];

      // Toggle the checked value
      setFilter(fieldKey, !fieldValue);
    },
    [setFilter, filters]
  );

  const handleClickClearFieldFn = useCallback<ClickClearFieldHandlerFactory>(
    (fieldKey: string) => (event: React.MouseEvent<HTMLInputElement>) => {
      event.preventDefault();

      // Clear multiple filters when date is cleared
      if (fieldKey === "date") {
        setFilters({
          ...filters,
          date: "",
          dateFrom: "",
          dateTo: ""
        });
      } else {
        setFilter(fieldKey, "");
      }
    },
    [setFilter, filters]
  );

  const handleClickResetFilters = useCallback<React.MouseEventHandler>(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setFilters({ ...DEFAULT_FILTERS });
    },
    [setFilters]
  );

  const handleClickToggleFilters = useCallback<React.MouseEventHandler>(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setShowFilters(!showFilters);
    },
    [showFilters]
  );

  const viewPage = useCallback(
    (pageNum: number) => {
      if (pageNum >= 1 && pageNum <= maxPages) {
        setPage(pageNum);
      }
    },
    [setPage]
  );

  // Event handlers
  const handleSelectPageFn = useCallback<React.ChangeEventHandler>(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      viewPage(Number(event.target.value));
    },
    [viewPage, page]
  );

  // @NOTE if you want to reinstate clicking a button to view a specific page,
  // uncomment this function.
  // const handleClickPageFn = useCallback<ClickPageNumberHandlerFactory>(
  //   (pageNum: number) => (event: React.MouseEvent) => {
  //     event.preventDefault();
  //     viewPage(pageNum);
  //   },
  //   [viewPage]
  // );

  const handleClickPageNext = useCallback<React.MouseEventHandler>(
    (event: React.MouseEvent) => {
      event.preventDefault();
      viewPage(page + 1);
    },
    [viewPage, page]
  );

  const handleClickPageBack = useCallback<React.MouseEventHandler>(
    (event: React.MouseEvent) => {
      event.preventDefault();
      viewPage(page - 1);
    },
    [viewPage, page]
  );

  const handleClickCloseViewCustomerId = () => {
    // Close the modal by setting this empty
    // The useAsync hook won't try to load the data again if the value is empty
    setViewCustomerId("");
  };

  // Mark visible unread notifications as read
  useLayoutEffect(() => {
    const visibleAndUnread = paginatedNotifications.filter(
      findByPropValueFn("read", false)
    );

    if (visibleAndUnread.length) {
      const visibleAndUnreadIds = visibleAndUnread.reduce(
        (acc: string[], n: INotification) => acc.concat([n.id]),
        []
      );
      notificationsStore.updateByQuery(
        {
          match: {
            id: {
              includesAny: visibleAndUnreadIds
            }
          }
        },
        { read: true }
      );
    }
  }, [page, paginatedNotifications]);

  return (
    <AppLayout headerLeftContent={<AppLayoutHeaderLeftCancel />}>
      <Page
        name="notifications"
        title={t("notifications.page.title")}
        headerContent={
          <Button
            label={
              showFilters
                ? t("notifications.filters.buttons.hideFilters.label")
                : t("notifications.filters.buttons.showFilters.label")
            }
            className="notifications-filters-toggle"
            shape="link"
            onClick={handleClickToggleFilters}
          />
        }
        footerContent={
          !!notifications.length &&
          maxPages > 1 && (
            <PaginationDropDown
              currentPage={page}
              maxPages={maxPages}
              onClickNext={handleClickPageNext}
              onClickBack={handleClickPageBack}
              onClickPageFn={handleSelectPageFn}
            />
          )
        }
      >
        <form
          className={cn(
            "notifications-filters-form",
            showFilters && "is-visible"
          )}
        >
          <Field
            type="text"
            id="filter-given-name"
            name="givenName"
            label={t("notifications.filters.fields.givenName.label")}
            value={filters.givenName}
            clearable
            onChange={handleChangeFieldInput}
            onClear={handleClickClearFieldFn("givenName")}
          />
          <Field
            type="text"
            id="filter-family-name"
            name="familyName"
            label={t("notifications.filters.fields.familyName.label")}
            value={filters.familyName}
            clearable
            onChange={handleChangeFieldInput}
            onClear={handleClickClearFieldFn("familyName")}
          />
          <Field
            type="select-custom"
            id="filter-date"
            name="date"
            label={t("notifications.filters.fields.date.label")}
            value={filters.date}
            clearable
            onChange={handleChangeInputSelectCustomFn("date")}
            inputProps={{
              sortOptionsAlphabetically: false,
              options: dateOptions,
              searchable: true
            }}
          />
          <Field
            type="select-custom"
            id="filter-type"
            name="statusType"
            label={t("notifications.filters.fields.statusType.label")}
            value={filters.statusType}
            clearable
            onChange={handleChangeInputSelectCustomFn("statusType")}
            inputProps={{
              sortOptionsAlphabetically: false,
              options: statusTypeOptions,
              searchable: true
            }}
          />
          <InputCheckbox
            id="filter-onlymine"
            name="onlyMine"
            className="filter-onlymine"
            label={t("notifications.filters.fields.showOnlyMine.label")}
            value={filters.onlyMine}
            onChange={handleChangeFieldInputCheckbox}
          />
        </form>
        <div className="notifications-notice">
          <p>
            {t("notifications.filters.notice", {
              numDays: NOTIFICATIONS_TIME_EXPIRES_IN_DAYS(config)
            })}
          </p>
        </div>
        <div key={page} className="notifications-list">
          {paginatedNotifications.length > 0 &&
            paginatedNotifications.map((n: INotification) => (
              <Notification
                formatter={
                  !formatter.loading && !formatter.error && !!formatter.value
                    ? formatter.value
                    : timeAgoFormatters.en
                }
                key={n.id}
                {...n}
                onDisplay={(customerId: string) => {
                  setViewCustomerId(customerId);
                }}
              />
            ))}

          {(!notifications.length || !paginatedNotifications.length) && (
            <div className="empty">
              {numAppliedFilters ? (
                <>
                  <p>{t("notifications.list.emptyWithFiltersApplied")}</p>
                  <Button
                    label={t(
                      "notifications.filters.buttons.clearFilters.label"
                    )}
                    shape="solid"
                    theme="primary"
                    onClick={handleClickResetFilters}
                  />
                </>
              ) : (
                <p>{t("notifications.list.empty")}</p>
              )}
            </div>
          )}
        </div>

        <Modal
          className="view-customer"
          isOpen={!!viewCustomerId}
          allowClose={true}
        >
          {customerData.loading && <LoadingScreen />}
          {customerData.error && (
            <div>
              <div className="error">
                {t("general.errors.unknownServerError", {
                  traceToken: ""
                })}
              </div>
              <Button
                label={t("general.buttons.close.label")}
                onClick={handleClickCloseViewCustomerId}
                className="button-role-close"
                theme="primary"
                shape="solid"
                fullWidth
              />
            </div>
          )}
          {customerData.value && (
            <div className="customer-data-modal">
              <Button
                className="button-role-close"
                icon="close"
                ariaLabel={t("general.buttons.close.label")}
                onClick={handleClickCloseViewCustomerId}
              />
              <dl>
                {Object.keys(customerData.value)
                  .filter(
                    /* filter unused labels & empty values */
                    keyName =>
                      customerData.value &&
                      customerData.value[keyName] &&
                      !FILTERED_LABELS.includes(keyName)
                  )
                  .map(keyName => {
                    let value = get(customerData.value, keyName);

                    /* use Google Sheet's translation keys (@TODO: harmonize translation keys with Configurable Form keys) */
                    let key = REWRITE_LABELS[keyName] || keyName;

                    /* get translated Country name from Country code */
                    if (
                      (key === "address.country" || key === "nationality") &&
                      countries.value
                    ) {
                      let country = countries.value.filter(country => {
                        return country.name === value;
                      })[0];

                      if (has(country, "geo.addressCountry")) {
                        const code = get(country, "geo.addressCountry");
                        value = t(code, {
                          defaultValue: value,
                          ns: "countries"
                        });
                      }
                    }

                    return (
                      value && (
                        <React.Fragment key={key}>
                          <dt
                            className={cn(
                              "summary-label",
                              CONSENT_KEYS.includes(key) &&
                                "summary-label-consent"
                            )}
                          >
                            {t(`customer.data.fields.${key}.label`)}
                          </dt>
                          <dd
                            className={cn(
                              "summary-value",
                              CONSENT_KEYS.includes(key) &&
                                "summary-value-consent"
                            )}
                          >
                            {CONSENT_KEYS.includes(key) && (
                              <>
                                <Icon name="check" />
                                <p className="sr-only">{String(value)}</p>
                              </>
                            )}
                            {!CONSENT_KEYS.includes(key) && value}
                          </dd>
                        </React.Fragment>
                      )
                    );
                  })}
              </dl>
              <Button
                label={t("general.buttons.close.label")}
                onClick={handleClickCloseViewCustomerId}
                className="button-role-close"
                theme="primary"
                shape="solid"
                fullWidth
              />
            </div>
          )}
        </Modal>
      </Page>
    </AppLayout>
  );
}
